import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Palette, 
  Save, 
  RefreshCw,
  Upload,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  ExternalLink,
  LayoutGrid,
  Star
} from 'lucide-react';
import PageSectionsManager from './PageSectionsManager';
import { BACKEND_URL } from '@/utils/apiConfig';
import ThemeIcon, { THEME_ICON_REGISTRY, THEME_ICON_KEYS } from '@/components/ThemeIcon';
import { useTheme } from '@/contexts/ThemeContext';

const API = `${BACKEND_URL}/api`;

const ThemeManager = () => {
  const token = localStorage.getItem('token');
  const { refreshStyles, setUserTheme } = useTheme();
  
  // State management
  const [selectedPage, setSelectedPage] = useState('home');
  const [pageStyles, setPageStyles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [messages, setMessages] = useState([]);
  
  // Preset state
  const [themePresets, setThemePresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [showCreatePreset, setShowCreatePreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  const [newPresetIcon, setNewPresetIcon] = useState('palette');
  const [presetScope, setPresetScope] = useState('global'); // 'global' or 'page'
  const [showPreview, setShowPreview] = useState(false);
  const [editingIconFor, setEditingIconFor] = useState(null); // theme id whose icon is being edited inline

  // Available pages (in order as they appear in navigation)
  const pages = [
    { value: 'home', label: 'Home' },
    { value: 'map', label: 'Map' },
    { value: 'dive_centers', label: 'Dive Centers' },
    { value: 'packages', label: 'Packages' },
    { value: 'gallery', label: 'Gallery' },
    { value: 'reviews', label: 'Reviews' },
    { value: 'contact', label: 'Contact' }
  ];

  // Fetch page styles on mount and when page changes
  useEffect(() => {
    fetchPageStyles(selectedPage);
    fetchThemePresets();
  }, [selectedPage]);

  const fetchPageStyles = async (pageName) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pages/${pageName}/styles`);
      setPageStyles(response.data);
    } catch (error) {
      console.error('Error fetching page styles:', error);
      showMessage('error', 'Failed to load page styles');
    } finally {
      setLoading(false);
    }
  };

  const fetchThemePresets = async () => {
    try {
      const response = await axios.get(`${API}/admin/themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThemePresets(response.data);
    } catch (error) {
      console.error('Error fetching theme presets:', error);
    }
  };

  const showMessage = (type, text, link = null) => {
    setMessages([{ type, text, link }]);
    setTimeout(() => setMessages([]), 8000);
  };

  const handleSaveStyles = async () => {
    try {
      setSaving(true);
      
      // Log what we're saving for debugging
      console.log('Saving page styles:', pageStyles);
      
      await axios.put(
        `${API}/admin/pages/${selectedPage}/styles`,
        pageStyles,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const pageUrl = selectedPage === 'home' ? '/' : `/${selectedPage}`;
      showMessage('success', `Styles saved successfully!`, pageUrl);
    } catch (error) {
      console.error('Error saving styles:', error);
      console.error('Error details:', error.response?.data);
      showMessage('error', 'Failed to save styles');
    } finally {
      setSaving(false);
    }
  };

  const handleResetStyles = async (pageName) => {
    const confirmMessage = pageName === 'all' 
      ? 'Are you sure you want to reset ALL pages to default styles? This cannot be undone!'
      : `Are you sure you want to reset the ${pageName} page to default styles? This cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSaving(true);
      
      await axios.delete(
        `${API}/admin/pages/${pageName}/styles/reset`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh the current page styles
      await fetchPageStyles(selectedPage);
      
      const message = pageName === 'all' 
        ? 'All pages reset to default styles successfully!'
        : `${pageName} page reset to default styles successfully!`;
      
      showMessage('success', message);
    } catch (error) {
      console.error('Error resetting styles:', error);
      showMessage('error', 'Failed to reset styles');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyPreset = async (themeId) => {
    try {
      setSaving(true);
      await axios.post(
        `${API}/admin/themes/${themeId}/apply`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchPageStyles(selectedPage);
      
      // Refresh the public-facing ThemeContext so gd_page_styles localStorage
      // reflects the newly-applied default. Without this, the admin's next
      // reload of a public page reads stale cached styles and flashes the
      // old theme before React catches up.
      await refreshStyles();
      
      // Clear any per-browser theme override the admin has set on themselves
      // so they immediately see the new default they just applied.
      setUserTheme(null);
      
      const themeName = themePresets.find(t => t.id === themeId)?.name || 'Theme';
      showMessage('success', `${themeName} applied to all pages successfully!`, '/');
      setSelectedPresetId(null);
    } catch (error) {
      console.error('Error applying preset:', error);
      showMessage('error', 'Failed to apply theme preset');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePreset = async () => {
    if (!newPresetName.trim()) {
      showMessage('error', 'Please enter a preset name');
      return;
    }

    try {
      setSaving(true);
      
      const presetData = {
        name: newPresetName,
        description: newPresetDescription || `Custom theme: ${newPresetName}`,
        preview_icon: newPresetIcon || 'palette',
        navbar_style: pageStyles.navbar,
        header_style: pageStyles.header,
        body_style: pageStyles.body,
        footer_style: pageStyles.footer
      };

      await axios.post(
        `${API}/admin/themes`,
        presetData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchThemePresets();
      
      showMessage('success', `Theme preset "${newPresetName}" created successfully!`);
      
      setShowCreatePreset(false);
      setNewPresetName('');
      setNewPresetDescription('');
      setNewPresetIcon('palette');
    } catch (error) {
      console.error('Error creating preset:', error);
      showMessage('error', 'Failed to create theme preset');
    } finally {
      setSaving(false);
    }
  };

  const handleReseedDefaults = async () => {
    if (!window.confirm('Reset default themes to the canonical "White Ocean" and "Kraken Dark"? This removes any legacy defaults (Ocean Blue, Tropical Paradise, Sunset Coral) and restores the two built-in themes. Custom presets you created are preserved.')) {
      return;
    }
    try {
      setSaving(true);
      const { data } = await axios.post(
        `${API}/admin/themes/reseed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchThemePresets();
      showMessage('success', `Default themes restored (${data.themes.join(', ')})`);
    } catch (error) {
      console.error('Error reseeding themes:', error);
      showMessage('error', 'Failed to reseed default themes');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePreviewIcon = async (themeId, newIcon) => {
    try {
      await axios.patch(
        `${API}/admin/themes/${themeId}`,
        { preview_icon: newIcon },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchThemePresets();
      setEditingIconFor(null);
      showMessage('success', `Preview icon updated`);
    } catch (error) {
      console.error('Error updating icon:', error);
      showMessage('error', 'Failed to update preview icon');
    }
  };

  const handleMediaUpload = async (file, component, altText, description) => {
    try {
      setUploadingMedia(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt_text', altText);
      formData.append('media_description', description);

      const response = await axios.post(
        `${API}/admin/upload-theme-media`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const mediaType = response.data.type === 'image' ? 'image' : 'video';
      const newUrl = BACKEND_URL + response.data.url;
      
      // Update page styles with new media
      setPageStyles(prev => ({
        ...prev,
        [component]: {
          ...prev[component],
          background_type: mediaType,
          background_value: newUrl,
          alt_text: altText,
          media_description: description
        }
      }));

      showMessage('success', 'Media uploaded successfully!');
      
      return { success: true, url: newUrl, type: mediaType };
    } catch (error) {
      console.error('Error uploading media:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to upload media');
      return { success: false };
    } finally {
      setUploadingMedia(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-slate-900">Theme Manager</h2>
        <p className="text-slate-600 mt-2">Customize your website&apos;s visual appearance.</p>
      </div>

      {/* Messages */}
      {messages.length > 0 && (
        <div className="space-y-2">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between p-4 rounded-lg ${
                msg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                msg.type === 'warning' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' :
                'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              <div className="flex items-center gap-3">
                {msg.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                <span className="font-medium">{msg.text}</span>
              </div>
              {msg.link && msg.type === 'success' && (
                <a 
                  href={msg.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  View Page
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="customize" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="customize">
            <Palette className="w-4 h-4 mr-2" />
            Page Customization
          </TabsTrigger>
          <TabsTrigger value="sections">
            <LayoutGrid className="w-4 h-4 mr-2" />
            Page Sections
          </TabsTrigger>
          <TabsTrigger value="presets" data-testid="theme-manager-tab-themes">
            <Sparkles className="w-4 h-4 mr-2" />
            Themes
          </TabsTrigger>
        </TabsList>

        {/* Page Customization Tab */}
        <TabsContent value="customize" className="space-y-6 mt-6">
          {/* Page Selector */}
          <Card>
            <CardHeader>
              <CardTitle>Select Page</CardTitle>
              <CardDescription>Choose which page to customize</CardDescription>
            </CardHeader>
            <CardContent>
              <select
                value={selectedPage}
                onChange={(e) => setSelectedPage(e.target.value)}
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {pages.map(page => (
                  <option key={page.value} value={page.value}>
                    {page.label}
                  </option>
                ))}
              </select>
            </CardContent>
          </Card>

          {/* Component Style Cards - Will be built in next steps */}
          {pageStyles && (
            <>
              <ComponentStyleCard 
                component="navbar"
                label="Navigation Bar"
                style={pageStyles.navbar}
                onUpdate={(updates) => setPageStyles(prev => ({
                  ...prev,
                  navbar: { ...prev.navbar, ...updates }
                }))}
                onMediaUpload={handleMediaUpload}
                uploadingMedia={uploadingMedia}
              />

              <ComponentStyleCard 
                component="header"
                label="Header / Hero Section"
                style={pageStyles.header}
                onUpdate={(updates) => setPageStyles(prev => ({
                  ...prev,
                  header: { ...prev.header, ...updates }
                }))}
                onMediaUpload={handleMediaUpload}
                uploadingMedia={uploadingMedia}
                includeTextFields={true}
              />

              <ComponentStyleCard 
                component="body"
                label="Main Body"
                style={pageStyles.body}
                onUpdate={(updates) => setPageStyles(prev => ({
                  ...prev,
                  body: { ...prev.body, ...updates }
                }))}
                onMediaUpload={handleMediaUpload}
                uploadingMedia={uploadingMedia}
              />

              <ComponentStyleCard 
                component="footer"
                label="Footer"
                style={pageStyles.footer}
                onUpdate={(updates) => setPageStyles(prev => ({
                  ...prev,
                  footer: { ...prev.footer, ...updates }
                }))}
                onMediaUpload={handleMediaUpload}
                uploadingMedia={uploadingMedia}
              />
            </>
          )}

          {/* Save and Reset Buttons */}
          <Card className="bg-cyan-50 border-cyan-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Ready to apply your changes?</h3>
                  <p className="text-sm text-slate-600">
                    Preview your changes before saving to the {selectedPage === 'home' ? 'homepage' : `${selectedPage} page`}
                  </p>
                </div>
                <div className="flex gap-3 ml-4">
                  <Button variant="outline" onClick={() => fetchPageStyles(selectedPage)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Discard
                  </Button>
                  <Button variant="outline" onClick={() => setShowPreview(true)} className="border-cyan-600 text-cyan-600 hover:bg-cyan-50">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  <Button onClick={handleSaveStyles} disabled={saving} size="lg" className="bg-cyan-600 hover:bg-cyan-700">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reset to Defaults Section */}
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-red-900 mb-1 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Reset to Default Styles
                  </h3>
                  <p className="text-sm text-red-700">
                    Remove all customizations and restore original default styles
                  </p>
                </div>
                <div className="flex gap-3 ml-4">
                  <Button 
                    variant="outline" 
                    onClick={() => handleResetStyles(selectedPage)}
                    className="border-red-600 text-red-600 hover:bg-red-100"
                  >
                    Reset This Page
                  </Button>
                  <Button 
                    onClick={() => handleResetStyles('all')}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Reset All Pages
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Page Sections Tab */}
        <TabsContent value="sections" className="space-y-6 mt-6">
          <PageSectionsManager />
        </TabsContent>

        {/* Presets Tab */}
        <TabsContent value="presets" className="space-y-6 mt-6">
          {/* Create New Preset */}
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    Create Your Own Preset
                  </CardTitle>
                  <CardDescription>Save your current customization as a reusable theme preset</CardDescription>
                </div>
                <Button 
                  onClick={() => setShowCreatePreset(!showCreatePreset)}
                  variant={showCreatePreset ? "secondary" : "default"}
                  className={!showCreatePreset ? "bg-purple-600 hover:bg-purple-700" : ""}
                >
                  {showCreatePreset ? 'Cancel' : 'Create New Preset'}
                </Button>
              </div>
            </CardHeader>
            {showCreatePreset && (
              <CardContent className="space-y-4">
                <div>
                  <Label>Preset Name *</Label>
                  <Input
                    value={newPresetName}
                    onChange={(e) => setNewPresetName(e.target.value)}
                    placeholder="e.g., Deep Ocean Blue"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Input
                    value={newPresetDescription}
                    onChange={(e) => setNewPresetDescription(e.target.value)}
                    placeholder="e.g., Professional deep blue theme with white accents"
                    className="bg-white"
                  />
                </div>

                {/* Preview Icon Picker */}
                <div className="bg-white p-4 rounded-lg border border-purple-200">
                  <Label className="mb-3 block font-semibold">Preview Icon <span className="text-xs font-normal text-slate-500">(shown in the public navbar theme switcher)</span></Label>
                  <div className="grid grid-cols-7 sm:grid-cols-10 gap-2" data-testid="create-preset-icon-grid">
                    {THEME_ICON_KEYS.map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setNewPresetIcon(key)}
                        className={`flex items-center justify-center aspect-square rounded-lg border-2 transition-all ${
                          newPresetIcon === key
                            ? 'border-purple-500 bg-purple-50 text-purple-700 scale-105 shadow-sm'
                            : 'border-slate-200 hover:border-purple-300 text-slate-600 hover:bg-slate-50'
                        }`}
                        title={THEME_ICON_REGISTRY[key].label}
                        aria-label={`Select ${THEME_ICON_REGISTRY[key].label} icon`}
                        data-testid={`create-preset-icon-${key}`}
                      >
                        <ThemeIcon name={key} size={20} />
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Selected: <span className="font-semibold text-purple-700">{THEME_ICON_REGISTRY[newPresetIcon]?.label || 'Palette'}</span>
                  </p>
                </div>
                
                {/* Preset Scope Toggle */}
                <div className="bg-white p-4 rounded-lg border border-purple-200">
                  <Label className="mb-3 block font-semibold">Preset Scope</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="presetScope" 
                        value="global"
                        checked={presetScope === 'global'}
                        onChange={(e) => setPresetScope(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-sm">Global (All pages)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="presetScope" 
                        value="page"
                        checked={presetScope === 'page'}
                        onChange={(e) => setPresetScope(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-sm">Page Specific ({selectedPage})</span>
                    </label>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {presetScope === 'global' 
                      ? '✨ This preset will apply to all pages when activated'
                      : `📄 This preset will only apply to the ${selectedPage} page`
                    }
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-slate-700 mb-2 font-medium">
                    📋 Preview of current styles:
                  </p>
                  <ul className="text-sm text-slate-600 space-y-1 ml-4">
                    <li>• Navigation: {pageStyles?.navbar?.background_type || 'default'}</li>
                    <li>• Header: {pageStyles?.header?.background_type || 'default'}</li>
                    <li>• Body: {pageStyles?.body?.background_type || 'default'}</li>
                    <li>• Footer: {pageStyles?.footer?.background_type || 'default'}</li>
                  </ul>
                </div>
                <Button 
                  onClick={handleCreatePreset}
                  disabled={saving || !newPresetName.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  size="lg"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {saving ? 'Creating...' : 'Save as New Preset'}
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Available Presets */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle>Available Themes</CardTitle>
                  <CardDescription>Select a theme to apply site-wide</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReseedDefaults}
                  disabled={saving}
                  data-testid="theme-manager-reseed-defaults-btn"
                  className="border-cyan-600 text-cyan-700 hover:bg-cyan-50"
                  title="Restores 'White Ocean' and 'Kraken Dark' as the canonical default themes. Legacy defaults are removed. Your custom themes are preserved."
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Default Themes
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {themePresets.map(preset => (
                  <Card 
                    key={preset.id} 
                    className={`cursor-pointer transition-all ${
                      selectedPresetId === preset.id 
                        ? 'border-2 border-cyan-500 bg-cyan-50 shadow-lg' 
                        : 'border-2 border-gray-200 hover:border-cyan-300'
                    }`}
                    onClick={() => setSelectedPresetId(preset.id)}
                    data-testid={`theme-preset-card-${preset.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          {/* Preview Icon (click to edit) */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingIconFor(editingIconFor === preset.id ? null : preset.id);
                            }}
                            className="flex items-center justify-center w-11 h-11 rounded-full border-2 border-cyan-400 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 transition-all flex-shrink-0"
                            title="Click to change preview icon"
                            aria-label="Change preview icon"
                            data-testid={`theme-preview-icon-btn-${preset.id}`}
                          >
                            <ThemeIcon name={preset.preview_icon || 'palette'} size={20} />
                          </button>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <CardTitle className="text-lg">{preset.name}</CardTitle>
                              {preset.is_default && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid={`theme-default-badge-${preset.id}`}>
                                  <Star className="w-3 h-3 fill-current" />
                                  Site Default
                                </span>
                              )}
                            </div>
                            <CardDescription>{preset.description}</CardDescription>
                          </div>
                        </div>
                        {selectedPresetId === preset.id && (
                          <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0" />
                        )}
                      </div>

                      {/* Inline icon picker */}
                      {editingIconFor === preset.id && (
                        <div
                          className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`theme-icon-editor-${preset.id}`}
                        >
                          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                            Choose preview icon
                          </div>
                          <div className="grid grid-cols-7 gap-1.5">
                            {THEME_ICON_KEYS.map((key) => (
                              <button
                                key={key}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdatePreviewIcon(preset.id, key);
                                }}
                                className={`flex items-center justify-center aspect-square rounded-md border transition-all ${
                                  (preset.preview_icon || 'palette') === key
                                    ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                                    : 'border-slate-200 hover:border-cyan-300 text-slate-600'
                                }`}
                                title={THEME_ICON_REGISTRY[key].label}
                                aria-label={`Set icon to ${THEME_ICON_REGISTRY[key].label}`}
                              >
                                <ThemeIcon name={key} size={16} />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2">
                        <div
                          className="w-12 h-12 rounded shadow-sm border border-gray-300"
                          style={{ background: preset.navbar_style.background_value }}
                          title="Navigation Bar"
                        />
                        <div
                          className="w-12 h-12 rounded shadow-sm border border-gray-300"
                          style={{ 
                            background: preset.header_style.background_value.includes('linear-gradient') 
                              ? preset.header_style.background_value
                              : preset.header_style.background_value.includes('from-') 
                                ? '#0891b2' 
                                : preset.header_style.background_value 
                          }}
                          title="Header"
                        />
                        <div
                          className="w-12 h-12 rounded shadow-sm border border-gray-300"
                          style={{ 
                            background: preset.body_style?.background_value || '#ffffff'
                          }}
                          title="Body"
                        />
                        <div
                          className="w-12 h-12 rounded shadow-sm border border-gray-300"
                          style={{ 
                            background: preset.footer_style.background_value.includes('linear-gradient')
                              ? preset.footer_style.background_value
                              : preset.footer_style.background_value 
                          }}
                          title="Footer"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Apply Preset Button */}
          <Card className="bg-cyan-50 border-cyan-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">
                    {selectedPresetId ? 'Apply selected theme to all pages' : 'Select a theme to apply'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {selectedPresetId 
                      ? 'This will update the styling for all pages.'
                      : 'Choose a theme preset above to get started.'
                    }
                  </p>
                </div>
                <div className="flex gap-3 ml-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setSelectedPresetId(null)}
                    disabled={!selectedPresetId}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset Selection
                  </Button>
                  <Button 
                    onClick={() => selectedPresetId && handleApplyPreset(selectedPresetId)}
                    disabled={saving || !selectedPresetId} 
                    size="lg" 
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {saving ? 'Applying...' : 'Apply Theme'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-bold">
                Preview - {pages.find(p => p.value === selectedPage)?.label || selectedPage}
              </h3>
              <button 
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <PreviewContent pageStyles={pageStyles} selectedPage={selectedPage} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Preview Component - Realistic preview of the actual page
const PreviewContent = ({ pageStyles, selectedPage }) => {
  if (!pageStyles) return null;

  // Get page-specific content
  const getPageContent = () => {
    switch(selectedPage) {
      case 'home':
        return {
          title: 'About Me',
          subtitle: 'Your Guide to the Underwater World',
          description: "With years of experience teaching divers of all levels, I'm passionate about sharing the magic of the ocean.",
          features: ['Professional Training', 'Global Locations', 'Expert Guidance']
        };
      case 'map':
        return {
          title: 'Dive Map',
          subtitle: 'Explore Dive Locations',
          description: 'Interactive map showing all dive centers and locations worldwide.',
          features: ['Interactive Map', 'Location Details', 'Current Position']
        };
      case 'dive_centers':
        return {
          title: 'Dive Centers',
          subtitle: 'Partner Facilities',
          description: 'World-class dive centers offering professional training and equipment.',
          features: ['Certified Centers', 'Equipment Rental', 'Training Programs']
        };
      case 'packages':
        return {
          title: 'Dive Packages',
          subtitle: 'Choose Your Adventure',
          description: 'Comprehensive dive packages for all skill levels and budgets.',
          features: ['Beginner Courses', 'Advanced Training', 'Custom Packages']
        };
      case 'gallery':
        return {
          title: 'Gallery',
          subtitle: 'Underwater Moments',
          description: 'Stunning photos and videos from our diving adventures.',
          features: ['Photo Gallery', 'Video Library', 'Recent Dives']
        };
      case 'reviews':
        return {
          title: 'Reviews',
          subtitle: 'What Divers Say',
          description: 'Read testimonials from satisfied divers around the world.',
          features: ['Customer Reviews', 'Ratings', 'Testimonials']
        };
      case 'contact':
        return {
          title: 'Contact',
          subtitle: 'Get In Touch',
          description: 'Have questions? Reach out and let\'s plan your next dive adventure.',
          features: ['Contact Form', 'Social Media', 'Direct Messaging']
        };
      default:
        return {
          title: 'Page Content',
          subtitle: 'Preview',
          description: 'This is a preview of your page styling.',
          features: ['Feature 1', 'Feature 2', 'Feature 3']
        };
    }
  };

  const pageContent = getPageContent();

  const getBackgroundStyle = (style) => {
    if (!style) return {};
    // Resolve relative URLs for preview
    const resolveUrl = (val) => val && val.startsWith('/api/') ? `${BACKEND_URL}${val}` : val;
    
    if (style.background_type === 'color') {
      return { backgroundColor: style.background_value };
    } else if (style.background_type === 'gradient') {
      if (style.background_value.includes('linear-gradient')) {
        return { background: style.background_value };
      } else if (style.background_value.includes('from-') && style.background_value.includes('to-')) {
        return { background: 'linear-gradient(to right, #0ea5e9, #06b6d4)' };
      } else {
        return { background: `linear-gradient(to right, ${style.background_value})` };
      }
    } else if (style.background_type === 'image') {
      return { 
        backgroundImage: `url(${resolveUrl(style.background_value)})`,
        backgroundSize: 'cover',
        backgroundPosition: style.image_position || 'center'
      };
    } else if (style.background_type === 'video') {
      return { position: 'relative', backgroundColor: '#1e293b' };
    }
    return {};
  };

  // Helper to render video background element
  const renderVideoBackground = (style) => {
    const resolveUrl = (val) => val && val.startsWith('/api/') ? `${BACKEND_URL}${val}` : val;
    if (style?.background_type === 'video' && style?.background_value) {
      return (
        <>
          <video 
            src={resolveUrl(style.background_value)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: style?.video_position || 'center',
              zIndex: 0
            }}
            autoPlay
            muted
            loop={style?.video_loop !== false}
            playsInline
            preload="auto"
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 1
          }}></div>
        </>
      );
    }
    return null;
  };

  const navStyle = {
    ...getBackgroundStyle(pageStyles.navbar),
    color: pageStyles.navbar?.text_color || '#1e293b',
  };

  const headerStyle = {
    ...getBackgroundStyle(pageStyles.header),
    color: pageStyles.header?.text_color || '#ffffff',
  };

  const bodyStyle = {
    ...getBackgroundStyle(pageStyles.body),
    color: pageStyles.body?.text_color || '#1e293b',
  };

  const footerStyle = {
    ...getBackgroundStyle(pageStyles.footer),
    color: pageStyles.footer?.text_color || '#ffffff',
  };

  return (
    <div className="border rounded-lg overflow-hidden" style={{ fontSize: '14px' }}>
      {/* Realistic Navbar Preview */}
      <div 
        style={{
          ...navStyle,
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {renderVideoBackground(pageStyles.navbar)}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative', zIndex: 2 }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            backgroundColor: '#0ea5e9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold'
          }}>G</div>
          <span style={{ fontWeight: 'bold', fontSize: '18px' }}>George Dives</span>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', position: 'relative', zIndex: 2 }}>
          <span style={{ cursor: 'pointer' }}>Home</span>
          <span style={{ cursor: 'pointer' }}>Map</span>
          <span style={{ cursor: 'pointer' }}>Packages</span>
          <span style={{ cursor: 'pointer' }}>Gallery</span>
          <span style={{ cursor: 'pointer' }}>Contact</span>
        </div>
      </div>

      {/* Realistic Header/Hero Preview */}
      <div 
        style={{
          ...headerStyle,
          padding: '6rem 2rem',
          minHeight: '500px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background image overlay */}
        {pageStyles.header?.background_type === 'image' && (
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${pageStyles.header.background_value?.startsWith('/api/') ? BACKEND_URL + pageStyles.header.background_value : pageStyles.header.background_value})`,
            backgroundSize: 'cover',
            backgroundPosition: pageStyles.header?.image_position || 'center',
            zIndex: 0
          }} />
        )}
        {/* Background video overlay */}
        {pageStyles.header?.background_type === 'video' && pageStyles.header?.background_value && (
          <video 
            src={pageStyles.header.background_value?.startsWith('/api/') ? BACKEND_URL + pageStyles.header.background_value : pageStyles.header.background_value}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: pageStyles.header?.video_position || 'center',
              zIndex: 0
            }}
            autoPlay
            muted
            loop={pageStyles.header?.video_loop !== false}
            playsInline
            preload="metadata"
          />
        )}
        {(pageStyles.header?.background_type === 'image' || pageStyles.header?.background_type === 'video') && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.3), rgba(0,0,0,0.5))',
            zIndex: 1
          }} />
        )}
        
        <div style={{ position: 'relative', zIndex: 2, maxWidth: '900px' }}>
          <h1 style={{ 
            fontSize: '3.5rem', 
            fontWeight: 'bold', 
            marginBottom: '1rem',
            lineHeight: '1.2'
          }}>
            {pageStyles.header?.title || 'Explore the Depths,'}
          </h1>
          <p style={{ 
            fontSize: '1.75rem', 
            marginBottom: '1.5rem',
            color: headerStyle.color || '#06b6d4',
            fontWeight: '600'
          }}>
            {pageStyles.header?.subtitle || 'Discover Your Passion'}
          </p>
          <p style={{ 
            fontSize: '1.125rem', 
            lineHeight: '1.6',
            maxWidth: '700px',
            margin: '0 auto',
            opacity: 0.9
          }}>
            {pageStyles.header?.description || 'Join me on an unforgettable underwater journey. From beginner certifications to professional training.'}
          </p>
        </div>
      </div>

      {/* Realistic Body Preview */}
      <div 
        style={{
          ...bodyStyle,
          padding: '4rem 2rem',
          minHeight: '400px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {renderVideoBackground(pageStyles.body)}
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          {/* Dynamic Page Content Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }}>
            <div>
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                backgroundColor: '#cffafe',
                color: '#0e7490',
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}>
                🏆 {pageContent.title}
              </div>
              <h2 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                marginBottom: '1.5rem',
                lineHeight: '1.2'
              }}>
                {pageContent.subtitle}
              </h2>
              <p style={{ 
                fontSize: '1.125rem', 
                lineHeight: '1.6',
                opacity: 0.8,
                marginBottom: '1rem'
              }}>
                {pageContent.description}
              </p>
              <p style={{ 
                fontSize: '1.125rem', 
                lineHeight: '1.6',
                opacity: 0.8
              }}>
                Experience world-class diving with professional guidance and top-tier equipment.
              </p>
            </div>
            <div style={{ 
              height: '350px', 
              borderRadius: '1rem',
              backgroundColor: 'rgba(6, 182, 212, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(6, 182, 212, 0.2)'
            }}>
              <span style={{ fontSize: '3rem' }}>
                {selectedPage === 'home' ? '🤿' : 
                 selectedPage === 'map' ? '🗺️' :
                 selectedPage === 'dive_centers' ? '🏢' :
                 selectedPage === 'packages' ? '📦' :
                 selectedPage === 'gallery' ? '📸' :
                 selectedPage === 'reviews' ? '⭐' :
                 selectedPage === 'contact' ? '✉️' : '🌊'}
              </span>
            </div>
          </div>

          {/* Features Grid */}
          <div style={{ marginTop: '4rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem' }}>
              {pageContent.features.map((feature, i) => (
                <div key={i} style={{ 
                  padding: '2rem',
                  borderRadius: '1rem',
                  backgroundColor: bodyStyle.color === '#1e293b' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  border: '1px solid ' + (bodyStyle.color === '#1e293b' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>
                    {i === 0 ? '📚' : i === 1 ? '🌍' : '🏅'}
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {feature}
                  </h3>
                  <p style={{ opacity: 0.7, lineHeight: '1.5' }}>
                    Professional diving services you can trust.
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Realistic Footer Preview */}
      <div 
        style={{
          ...footerStyle,
          padding: '3rem 2rem',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {renderVideoBackground(pageStyles.footer)}
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem', marginBottom: '2rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '50%', 
                  backgroundColor: '#0ea5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.25rem'
                }}>G</div>
                <span style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>George Dives</span>
              </div>
              <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                Bringing you the best diving experiences around the world
              </p>
            </div>
            <div>
              <h4 style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Quick Links</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>
                <span>Home</span>
                <span>Packages</span>
                <span>Gallery</span>
                <span>Contact</span>
              </div>
            </div>
            <div>
              <h4 style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Contact</h4>
              <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                <p>Email: info@georgedives.com</p>
                <p>WhatsApp: +30 698 794 6278</p>
              </div>
            </div>
          </div>
          <div style={{ 
            borderTop: '1px solid rgba(255,255,255,0.1)', 
            paddingTop: '2rem', 
            textAlign: 'center',
            fontSize: '0.875rem',
            opacity: 0.6
          }}>
            © {new Date().getFullYear()} George Dives. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

// Component Style Card - Individual card for each component (navbar, header, body, footer)
const ComponentStyleCard = ({ component, label, style, onUpdate, onMediaUpload, uploadingMedia, includeTextFields = false }) => {
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [mediaAltText, setMediaAltText] = useState('');
  const [mediaDescription, setMediaDescription] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mediaSelectionMode, setMediaSelectionMode] = useState('upload'); // 'upload', 'url', or 'preset'
  const [presetMedia, setPresetMedia] = useState({ images: [], videos: [] });
  const [loadingPresets, setLoadingPresets] = useState(false);

  // Fetch preset media when switching to preset mode
  const fetchPresetMedia = async () => {
    try {
      setLoadingPresets(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/preset-media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPresetMedia(response.data);
    } catch (error) {
      console.error('Error fetching preset media:', error);
    } finally {
      setLoadingPresets(false);
    }
  };

  // Handle preset media selection — store RELATIVE URL (not absolute) for portability
  const handlePresetSelect = (presetUrl, filename) => {
    onUpdate({ 
      background_value: presetUrl,
      alt_text: filename.replace(/\.(jpg|jpeg|png|webp|gif|mp4|webm|mov)$/i, '').replace(/[-_]/g, ' '),
      media_description: `Preset media: ${filename}`
    });
    setMediaSelectionMode('upload'); // Reset to upload mode after selection
  };

  if (!style) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      setShowMediaUpload(true);
      
      // Create local preview URL
      const localUrl = URL.createObjectURL(file);
      setPreviewUrl(localUrl);
    }
  };

  const handleUpload = async () => {
    if (uploadFile && mediaAltText.trim()) {
      const result = await onMediaUpload(uploadFile, component, mediaAltText, mediaDescription);
      if (result.success) {
        setShowMediaUpload(false);
        setUploadFile(null);
        setMediaAltText('');
        setMediaDescription('');
        setPreviewUrl(null);
      }
    }
  };

  // Clear old media data when switching background type away from image/video
  const handleBackgroundTypeChange = (newType) => {
    onUpdate({ background_type: newType });
    
    // Clear media-related fields when switching away from image/video
    if (newType !== 'image' && newType !== 'video') {
      onUpdate({ 
        background_type: newType,
        background_value: newType === 'color' ? '#ffffff' : 'linear-gradient(to right, #0ea5e9, #06b6d4)',
        alt_text: null,
        media_description: null
      });
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{label}</CardTitle>
        <CardDescription>Customize the appearance of the {label.toLowerCase()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Background Type Selection */}
        <div>
          <Label>Background Type</Label>
          <select
            value={style.background_type}
            onChange={(e) => handleBackgroundTypeChange(e.target.value)}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="color">Solid Color</option>
            <option value="gradient">Gradient</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
        </div>

        {/* Solid Color */}
        {style.background_type === 'color' && (
          <div>
            <Label>Background Color</Label>
            <div className="flex gap-3">
              <Input
                type="text"
                value={style.background_value}
                onChange={(e) => onUpdate({ background_value: e.target.value })}
                placeholder="#ffffff or rgba(255,255,255,0.95)"
                className="flex-1"
              />
              <input
                type="color"
                value={style.background_value.startsWith('#') ? style.background_value : '#ffffff'}
                onChange={(e) => onUpdate({ background_value: e.target.value })}
                className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
              />
            </div>
          </div>
        )}

        {/* Gradient */}
        {style.background_type === 'gradient' && (
          <div className="space-y-3">
            <Label>Gradient Colors (2-3 colors)</Label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm">Start Color</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    defaultValue="#1e3a8a"
                    id={`${component}-gradient-start-color`}
                    onChange={(e) => {
                      const colors = [
                        e.target.value,
                        document.getElementById(`${component}-gradient-mid-color`)?.value || '#0e7490',
                        document.getElementById(`${component}-gradient-end-color`)?.value || '#1e3a8a'
                      ];
                      onUpdate({ background_value: `linear-gradient(to right, ${colors.join(', ')})` });
                    }}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    type="text"
                    defaultValue="#1e3a8a"
                    id={`${component}-gradient-start-text`}
                    onBlur={(e) => {
                      const colors = [
                        e.target.value,
                        document.getElementById(`${component}-gradient-mid-text`)?.value || '#0e7490',
                        document.getElementById(`${component}-gradient-end-text`)?.value || '#1e3a8a'
                      ];
                      onUpdate({ background_value: `linear-gradient(to right, ${colors.join(', ')})` });
                    }}
                    placeholder="#1e3a8a"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Mid Color (optional)</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    defaultValue="#0e7490"
                    id={`${component}-gradient-mid-color`}
                    onChange={(e) => {
                      const colors = [
                        document.getElementById(`${component}-gradient-start-color`)?.value || '#1e3a8a',
                        e.target.value,
                        document.getElementById(`${component}-gradient-end-color`)?.value || '#1e3a8a'
                      ];
                      onUpdate({ background_value: `linear-gradient(to right, ${colors.join(', ')})` });
                    }}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    type="text"
                    defaultValue="#0e7490"
                    id={`${component}-gradient-mid-text`}
                    onBlur={(e) => {
                      const colors = [
                        document.getElementById(`${component}-gradient-start-text`)?.value || '#1e3a8a',
                        e.target.value,
                        document.getElementById(`${component}-gradient-end-text`)?.value || '#1e3a8a'
                      ];
                      onUpdate({ background_value: `linear-gradient(to right, ${colors.join(', ')})` });
                    }}
                    placeholder="#0e7490"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">End Color</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    defaultValue="#1e3a8a"
                    id={`${component}-gradient-end-color`}
                    onChange={(e) => {
                      const colors = [
                        document.getElementById(`${component}-gradient-start-color`)?.value || '#1e3a8a',
                        document.getElementById(`${component}-gradient-mid-color`)?.value || '#0e7490',
                        e.target.value
                      ];
                      onUpdate({ background_value: `linear-gradient(to right, ${colors.join(', ')})` });
                    }}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    type="text"
                    defaultValue="#1e3a8a"
                    id={`${component}-gradient-end-text`}
                    onBlur={(e) => {
                      const colors = [
                        document.getElementById(`${component}-gradient-start-text`)?.value || '#1e3a8a',
                        document.getElementById(`${component}-gradient-mid-text`)?.value || '#0e7490',
                        e.target.value
                      ];
                      onUpdate({ background_value: `linear-gradient(to right, ${colors.join(', ')})` });
                    }}
                    placeholder="#1e3a8a"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              💡 Use the color pickers for quick selection or type hex/rgba values
            </p>
            <div>
              <Label className="text-sm">Or paste full gradient CSS</Label>
              <Input
                type="text"
                value={style.background_value}
                onChange={(e) => onUpdate({ background_value: e.target.value })}
                placeholder="linear-gradient(to right, #0ea5e9, #06b6d4)"
                className="mt-1"
              />
            </div>
          </div>
        )}

        {/* Image/Video Upload */}
        {(style.background_type === 'image' || style.background_type === 'video') && (
          <div className="space-y-4">
            {/* Current Media Preview - Always show if there's a value */}
            {(style.background_value || previewUrl) && style.background_value !== '' && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <Label className="text-sm font-semibold text-slate-700">
                  Current {style.background_type === 'image' ? 'Image' : 'Video'}
                </Label>
                <div className="mt-2 relative w-48 h-32 border-2 border-cyan-400 rounded-lg overflow-hidden bg-gray-900 shadow-md">
                  {style.background_type === 'image' ? (
                    <img 
                      src={previewUrl || style.background_value}
                      alt={style.alt_text || 'Current background'}
                      className="w-full h-full object-cover"
                      onLoad={() => console.log('Image loaded:', component)}
                      onError={(e) => {
                        console.error('Image load error:', component, style.background_value);
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <video 
                      key={style.background_value}
                      src={previewUrl || style.background_value}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: style.video_position || 'center' }}
                      muted
                      autoPlay
                      loop={style.video_loop !== false}
                      playsInline
                      preload="auto"
                      onLoadedData={() => console.log('Video loaded:', component, style.background_value)}
                      onError={(e) => {
                        console.error('Video load error:', component, style.background_value);
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  )}
                  {/* Error fallback */}
                  <div 
                    style={{ display: 'none' }}
                    className="absolute inset-0 flex items-center justify-center bg-gray-800 text-gray-300 text-sm flex-col gap-2"
                  >
                    <span className="text-2xl">{style.background_type === 'image' ? '🖼️' : '🎥'}</span>
                    <span>Loading...</span>
                  </div>
                </div>
                {style.alt_text && (
                  <p className="text-xs text-slate-600 mt-2 font-medium">
                    📝 Alt: {style.alt_text}
                  </p>
                )}
                {style.media_description && (
                  <p className="text-xs text-slate-500 mt-1">
                    📄 Description: {style.media_description}
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-1 break-all">
                  🔗 URL: {(previewUrl || style.background_value).substring(0, 60)}...
                </p>
              </div>
            )}

            {/* Media Selection Mode Tabs */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
              <Label className="font-semibold text-slate-900 mb-3 block">
                Choose Media Source
              </Label>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setMediaSelectionMode('upload')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    mediaSelectionMode === 'upload'
                      ? 'bg-cyan-600 text-white shadow-md'
                      : 'bg-white text-slate-700 hover:bg-cyan-50 border border-slate-200'
                  }`}
                >
                  📤 Upload
                </button>
                <button
                  type="button"
                  onClick={() => setMediaSelectionMode('url')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    mediaSelectionMode === 'url'
                      ? 'bg-cyan-600 text-white shadow-md'
                      : 'bg-white text-slate-700 hover:bg-cyan-50 border border-slate-200'
                  }`}
                >
                  🔗 URL
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMediaSelectionMode('preset');
                    fetchPresetMedia();
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    mediaSelectionMode === 'preset'
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-white text-slate-700 hover:bg-purple-50 border border-slate-200'
                  }`}
                >
                  ✨ Preset
                </button>
              </div>

              {/* Upload Mode */}
              {mediaSelectionMode === 'upload' && (
                <div className="space-y-3">
                  <div>
                    <Label>Upload New {style.background_type === 'image' ? 'Image' : 'Video'}</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        type="file"
                        accept={style.background_type === 'image' ? 'image/*' : 'video/*'}
                        onChange={handleFileSelect}
                        className="flex-1 bg-white"
                      />
                      {(style.background_value || previewUrl) && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            onUpdate({ 
                              background_value: '',
                              alt_text: null,
                              media_description: null
                            });
                            setPreviewUrl(null);
                            setUploadFile(null);
                            setShowMediaUpload(false);
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* URL Mode */}
              {mediaSelectionMode === 'url' && (
                <div>
                  <Label>Enter Media URL</Label>
                  <Input
                    value={style.background_value || ''}
                    onChange={(e) => onUpdate({ background_value: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    className="mt-2 bg-white"
                  />
                  <p className="text-xs text-slate-600 mt-2">
                    💡 Paste a URL to use an external image/video
                  </p>
                </div>
              )}

              {/* Preset Mode */}
              {mediaSelectionMode === 'preset' && (
                <div>
                  <Label className="mb-2 block">Select from Preset Library</Label>
                  {loadingPresets ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
                      <span className="ml-2 text-sm text-slate-600">Loading presets...</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {style.background_type === 'image' && presetMedia.images.length > 0 && (
                        <div className="grid grid-cols-3 gap-3 max-h-64 overflow-y-auto bg-white p-3 rounded-lg border border-slate-200">
                          {presetMedia.images.map((preset, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handlePresetSelect(preset.url, preset.filename)}
                              className="relative aspect-video rounded-lg overflow-hidden border-2 border-slate-200 hover:border-purple-500 transition-all group"
                            >
                              <img 
                                src={BACKEND_URL + preset.url} 
                                alt={preset.display_name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                                <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity px-2 text-center">
                                  {preset.display_name}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {style.background_type === 'video' && presetMedia.videos.length > 0 && (
                        <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto bg-white p-3 rounded-lg border border-slate-200">
                          {presetMedia.videos.map((preset, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handlePresetSelect(preset.url, preset.filename)}
                              className="relative aspect-video rounded-lg overflow-hidden border-2 border-slate-200 hover:border-purple-500 transition-all group bg-gray-900"
                            >
                              <video 
                                src={BACKEND_URL + preset.url} 
                                className="w-full h-full object-cover"
                                muted
                                loop
                                playsInline
                                onMouseEnter={(e) => e.target.play()}
                                onMouseLeave={(e) => e.target.pause()}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                                <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity px-2 text-center">
                                  🎥 {preset.display_name}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {((style.background_type === 'image' && presetMedia.images.length === 0) ||
                        (style.background_type === 'video' && presetMedia.videos.length === 0)) && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                          <p className="text-sm text-yellow-800 mb-2">
                            📁 No preset {style.background_type === 'image' ? 'images' : 'videos'} available yet
                          </p>
                          <p className="text-xs text-yellow-700">
                            Add {style.background_type === 'image' ? 'images' : 'videos'} to <code>/backend/uploads/preset_media/{style.background_type === 'image' ? 'images' : 'videos'}/</code> to use them here
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Upload Progress Modal */}
            {showMediaUpload && uploadFile && (
              <div className="bg-blue-50 p-4 rounded-lg space-y-3 border border-blue-200">
                <p className="text-sm font-medium text-blue-900">
                  📎 Selected: {uploadFile.name}
                </p>
                <div>
                  <Label>Alt Text (Required for SEO) *</Label>
                  <Input
                    value={mediaAltText}
                    onChange={(e) => setMediaAltText(e.target.value)}
                    placeholder="Describe the image for accessibility and SEO"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Textarea
                    value={mediaDescription}
                    onChange={(e) => setMediaDescription(e.target.value)}
                    placeholder="Additional description for SEO"
                    rows={2}
                    className="bg-white"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleUpload} 
                    disabled={!mediaAltText || uploadingMedia}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingMedia ? 'Uploading...' : 'Upload Media'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowMediaUpload(false);
                      setUploadFile(null);
                      setMediaAltText('');
                      setMediaDescription('');
                      setPreviewUrl(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Media Settings - For images and videos */}
            {(style.background_type === 'image' || style.background_type === 'video') && (
              <div className="space-y-4">
                {/* Loop Toggle - Only for videos */}
                {style.background_type === 'video' && (
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <Label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={style.video_loop !== false}
                        onChange={(e) => onUpdate({ video_loop: e.target.checked })}
                        className="w-5 h-5 rounded text-purple-600 focus:ring-2 focus:ring-purple-500"
                      />
                      <div>
                        <span className="font-semibold text-slate-900">Loop Video</span>
                        <p className="text-xs text-slate-600 mt-1">
                          {style.video_loop !== false 
                            ? '🔁 Video will replay continuously' 
                            : '▶️ Video will play once and stop'}
                        </p>
                      </div>
                    </Label>
                  </div>
                )}

                {/* Position/Cropping - For both images and videos */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <Label className="font-semibold text-slate-900 mb-3 block">
                    🎯 {style.background_type === 'image' ? 'Image' : 'Video'} Position & Cropping
                  </Label>
                  <p className="text-xs text-slate-600 mb-3">
                    Choose which part of the {style.background_type === 'image' ? 'image' : 'video'} is visible in the frame
                  </p>
                  
                  {/* Preset Positions */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[
                      { value: 'top', label: 'Top', icon: '⬆️' },
                      { value: 'center', label: 'Center', icon: '🎯' },
                      { value: 'bottom', label: 'Bottom', icon: '⬇️' },
                      { value: 'left', label: 'Left', icon: '⬅️' },
                      { value: 'center', label: 'Center', icon: '⚫' },
                      { value: 'right', label: 'Right', icon: '➡️' }
                    ].map((pos, idx) => {
                      const positionKey = style.background_type === 'image' ? 'image_position' : 'video_position';
                      const currentPosition = style[positionKey] || 'center';
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => onUpdate({ [positionKey]: pos.value })}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            currentPosition === pos.value
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-white text-slate-700 hover:bg-blue-100 border border-blue-200'
                          }`}
                        >
                          <span className="block text-base">{pos.icon}</span>
                          <span className="block text-xs">{pos.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Custom Position */}
                  <details className="mt-3">
                    <summary className="text-sm font-medium text-blue-700 cursor-pointer hover:text-blue-800">
                      ⚙️ Advanced: Custom Position
                    </summary>
                    <div className="mt-3 space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Horizontal (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            defaultValue="50"
                            id={`${component}-h-pos`}
                            onChange={(e) => {
                              const h = e.target.value;
                              const v = document.getElementById(`${component}-v-pos`)?.value || '50';
                              const positionKey = style.background_type === 'image' ? 'image_position' : 'video_position';
                              onUpdate({ [positionKey]: `${h}% ${v}%` });
                            }}
                            className="mt-1 bg-white"
                            placeholder="50"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Vertical (%)</Label>
                          <Input
                            id={`${component}-v-pos`}
                            type="number"
                            min="0"
                            max="100"
                            defaultValue="50"
                            onChange={(e) => {
                              const v = e.target.value;
                              const h = document.getElementById(`${component}-h-pos`)?.value || '50';
                              const positionKey = style.background_type === 'image' ? 'image_position' : 'video_position';
                              onUpdate({ [positionKey]: `${h}% ${v}%` });
                            }}
                            className="mt-1 bg-white"
                            placeholder="50"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">
                        💡 0% = top/left, 50% = center, 100% = bottom/right
                      </p>
                    </div>
                  </details>
                  
                  <div className="mt-3 text-xs text-slate-500">
                    Current: <span className="font-mono font-semibold">
                      {style.background_type === 'image' 
                        ? (style.image_position || 'center')
                        : (style.video_position || 'center')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Text Color */}
        <div>
          <Label>Text Color</Label>
          <div className="flex gap-3">
            <Input
              type="text"
              value={style.text_color || ''}
              onChange={(e) => onUpdate({ text_color: e.target.value })}
              placeholder="#ffffff"
              className="flex-1"
            />
            <input
              type="color"
              value={(style.text_color && style.text_color.startsWith('#')) ? style.text_color : '#ffffff'}
              onChange={(e) => onUpdate({ text_color: e.target.value })}
              className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
            />
          </div>
        </div>

        {/* Header-specific Text Fields */}
        {includeTextFields && (
          <>
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-slate-900 mb-3">Header Text Content</h4>
            </div>
            
            {/* Title */}
            <div>
              <Label>Title</Label>
              <Input
                value={style.title || ''}
                onChange={(e) => onUpdate({ title: e.target.value })}
                placeholder="Welcome to George Dives"
              />
              <div className="flex gap-3 mt-2">
                <Input
                  type="text"
                  value={style.title_color || ''}
                  onChange={(e) => onUpdate({ title_color: e.target.value })}
                  placeholder="#ffffff"
                  className="flex-1"
                />
                <input
                  type="color"
                  value={(style.title_color && style.title_color.startsWith('#')) ? style.title_color : '#ffffff'}
                  onChange={(e) => onUpdate({ title_color: e.target.value })}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                  title="Title color"
                />
              </div>
            </div>
            
            {/* Subtitle */}
            <div>
              <Label>Subtitle</Label>
              <Input
                value={style.subtitle || ''}
                onChange={(e) => onUpdate({ subtitle: e.target.value })}
                placeholder="Discover Your Passion"
              />
              <div className="flex gap-3 mt-2">
                <Input
                  type="text"
                  value={style.subtitle_color || ''}
                  onChange={(e) => onUpdate({ subtitle_color: e.target.value })}
                  placeholder="#06b6d4"
                  className="flex-1"
                />
                <input
                  type="color"
                  value={(style.subtitle_color && style.subtitle_color.startsWith('#')) ? style.subtitle_color : '#06b6d4'}
                  onChange={(e) => onUpdate({ subtitle_color: e.target.value })}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                  title="Subtitle color"
                />
              </div>
            </div>
            
            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={style.description || ''}
                onChange={(e) => onUpdate({ description: e.target.value })}
                placeholder="Additional description text"
                rows={3}
              />
              <div className="flex gap-3 mt-2">
                <Input
                  type="text"
                  value={style.description_color || ''}
                  onChange={(e) => onUpdate({ description_color: e.target.value })}
                  placeholder="#ffffff"
                  className="flex-1"
                />
                <input
                  type="color"
                  value={(style.description_color && style.description_color.startsWith('#')) ? style.description_color : '#ffffff'}
                  onChange={(e) => onUpdate({ description_color: e.target.value })}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                  title="Description color"
                />
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ThemeManager;
